import { Lib } from "albertine-shared-web";
import { addHours, addMinutes, startOfDay } from "date-fns";
import type { Agent } from "../types/Agent";
import type { RequestDeadlineStatus, StatusDeadline } from "../types/Deadline";
import type { FirebaseTimestamp } from "../types/FirebaseTimestamp";
import {
    CategoriesWithUncategorized,
    categoryDisplayName,
} from "../types/OfferingCategory";
import type { Request } from "../types/Request";
import { RequestBookingOutcomeMap, RequestOutcomeMap } from "../types/RequestOutcome";
import { unassignedAgent } from "./agents.util";
import { fromArrayToMap } from "./array.util";

export function isActiveRequest(request: Request) {
    return request.status !== "closed";
}

export function memberRequestCount(
    requests: Request[] | undefined,
    memberId: string | undefined,
) {
    if (!memberId || !requests || requests.length === 0)
        return {
            total: 0,
            active: 0,
        };
    const requestByMember: Request[] =
        requests
            .filter((request) => request.member === memberId)
            .filter((request) => !request.archived) || [];
    return requestByMember.reduce(
        (prev, curr) => ({
            ...prev,
            active: isActiveRequest(curr) ? prev.active + 1 : prev.active,
        }),
        {
            total: requestByMember.length,
            active: 0,
        },
    );
}

export function showRequestAsOverdue(request: Request) {
    return (
        (request.status === "open" &&
            request.nextDeadlineStep !== "wait_for_member_response") ||
        (request.status === "confirmed" &&
            request.nextDeadlineStep !== "no_further_action")
    );
}

export function filterOverdueRequests(
    requests: Request[] | undefined,
    fromTimestampToDate: (value: FirebaseTimestamp) => Date,
    now: Date | undefined = undefined,
): Request[] {
    if (!requests || requests.length === 0) return [];
    return requests.filter(
        (req) =>
            req.nextDeadlineAt &&
            showRequestAsOverdue(req) &&
            fromTimestampToDate(req.nextDeadlineAt) < addMinutes(now ?? new Date(), 1),
    );
}

export function filterOverdueRequestsToday(
    requests: Request[] | undefined,
    fromTimestampToDate: (value: FirebaseTimestamp) => Date,
    now: Date | undefined = undefined,
): Request[] {
    if (!requests || requests.length === 0) return [];
    return requests.filter(
        (req) =>
            req.nextDeadlineAt &&
            showRequestAsOverdue(req) &&
            fromTimestampToDate(req.nextDeadlineAt) <
                addMinutes(now ?? new Date(), 1) &&
            fromTimestampToDate(req.nextDeadlineAt) > startOfDay(now ?? new Date()),
    );
}

export function requestAssignees(requests: Request[], agents: Agent[]) {
    return [unassignedAgent, ...agents].map((agent) => ({
        assignee: agent,
        count: requests
            .map((it) => ({
                ...it,
                assignedTo: it.assignedTo ?? unassignedAgent.id,
            }))
            .filter((it) => it.assignedTo === agent.id).length,
    }));
}

export function sortByNextDeadlineAt(
    requests: Request[],
    fromTimestampToDate: (value: FirebaseTimestamp) => Date,
): Request[] {
    return requests.sort(
        Lib.Utils.Sorting.sortByDateComparator<Request>((req) =>
            req?.nextDeadlineAt ? fromTimestampToDate(req.nextDeadlineAt) : undefined,
        ),
    );
}

export type CategoryRequestMap = Record<
    CategoriesWithUncategorized,
    {
        id: CategoriesWithUncategorized;
        name: string;
        items: Request[];
    }
>;

export function requestsByCategories(requests: Request[]): CategoryRequestMap {
    const result: CategoryRequestMap = fromArrayToMap(
        CategoriesWithUncategorized.options.map((category) => ({
            id: category,
            name: categoryDisplayName(category, "en"),
            items: requests.filter((req) => req.offeringCategory === category),
        })),
    );
    result.uncategorized = {
        id: "uncategorized",
        name: "Uncategorized",
        items: requests.filter((req) => !req.offeringCategory),
    };
    return result;
}

export const requestCategoryMatchesOnAny =
    (categories: CategoriesWithUncategorized[]) => (request: Request) => {
        if (categories.length === 0) {
            return true;
        }

        return request.offeringCategory
            ? categories.includes(request.offeringCategory)
            : categories.includes("uncategorized");
    };

export const requestAssignedToMatchesOnAny =
    (assignedTo: string[]) => (request: Request) => {
        if (assignedTo.length === 0) {
            return true;
        }

        if (request.assignedTo) {
            return assignedTo.includes(request.assignedTo);
        }

        return assignedTo.includes(unassignedAgent.id);
    };

export const requestNoFurtherAction = (request: Request) => {
    if (
        !request.nextDeadlineStep &&
        request.outcome &&
        request.status === "confirmed" &&
        request.outcome === "noActionNeeded"
    )
        return true;
    return false;
};

export const requestToStatusDeadlineKey = (
    request: Request,
): StatusDeadline | undefined => {
    const noFurtherAction =
        request.nextDeadlineStep && request.nextDeadlineStep === "no_further_action";
    if (
        (request.status === "open" || request.status === "confirmed") &&
        request.nextDeadlineStep &&
        !noFurtherAction
    ) {
        return `${request.status}__${request.nextDeadlineStep}`;
    }
    if (
        (request.status === "confirmed" || request.status === "closed") &&
        request.bookingOutcome
    ) {
        return `${request.status}__${request.bookingOutcome}`;
    }
    if (
        (request.status === "confirmed" || request.status === "closed") &&
        request.outcome
    ) {
        return `${request.status}__${request.outcome}`;
    }

    return undefined;
};

export const requestMatchesStatusDeadline =
    (statusFilter: RequestDeadlineStatus[]) => (request: Request) => {
        if (statusFilter.length === 0) {
            return true;
        }
        if (statusFilter.includes("draft") && request.status === "draft") {
            return true;
        }
        if (statusFilter.includes("new") && request.status === "new") {
            return true;
        }

        const key = requestToStatusDeadlineKey(request);
        return key && statusFilter.includes(key);
    };

export type IsActionable = "actionable" | "non-actionable";
export function requestIsActionable(request: Request): IsActionable {
    return request.nextDeadlineStep === "wait_for_member_response"
        ? "non-actionable"
        : "actionable";
}

export function requestOutcome(request: Request): string | undefined {
    if (request.status !== "confirmed" && request.status !== "closed") return undefined;
    if (
        request.bookingOutcome &&
        (request.outcome === "booked" || request.outcome === "noActionNeeded")
    ) {
        return RequestBookingOutcomeMap[request.bookingOutcome];
    }
    if (request.outcome === "noActionNeeded") return undefined;
    if (request.outcome) {
        return RequestOutcomeMap[request.outcome];
    }
    return undefined;
}

export function formatTicketNumber(ticketNumber: number | null): string {
    if (!ticketNumber) {
        return "X";
    }

    if (ticketNumber < 10) {
        return `00${ticketNumber}`;
    }

    if (ticketNumber < 100) {
        return `0${ticketNumber}`;
    }

    return ticketNumber.toString();
}
